import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function PrivatRengoeringPage() {
  return (
    <>
      <SEO
        title="Privat rengøring"
        description="Professionel privat rengøring i Havdrup og omegn. Få et rent og indbydende hjem med fleksible aftaler tilpasset dine ønsker og behov."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d3836]/10 to-white py-12 sm:py-16 lg:py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-slate-800 transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span>Ydelser</span>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="font-semibold text-slate-900">Privat rengøring</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0d3836]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0d3836]">
                <i className="fa-regular fa-house-chimney text-[#0d3836]" aria-hidden="true" />
                Privat rengøring
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Professionel rengøring af dit private hjem
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Få mere tid i hverdagen med professionel rengøring af hjemmet. Vi tilpasser rengøringen efter boligens størrelse, dine ønsker og hvor ofte du ønsker hjælp.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#154e4a] transition-colors text-center"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
                >
                  <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
                  <span className="no-break">31 34 16 32</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-md border border-slate-200/80 aspect-4/3">
                <img
                  src={imageUrl('/_assets/shutterstock-2441536685.jpg', 'width=800,quality=85,format=auto')}
                  alt="Grundig gulvvask og rengøring i moderne stue"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detaljeret gennemgang */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Et rent hjem med faste eller fleksible aftaler
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                  <p>
                    Vi ved, at behovet for rengøring ikke er ens fra kunde til kunde. Nogle ønsker hjælp hver uge, mens andre har brug for en grundig omgang hver 14. dag eller en månedlig opfriskning.
                  </p>
                  <p>
                    Uanset hvad du foretrækker, lægger vi vægt på ordentlighed og stabilitet. Vi møder til tiden, overholder vores aftaler og gør os umage med hver eneste detalje.
                  </p>
                </div>
              </div>

              {/* Hvad inkluderer vi typisk */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-4 flex items-center gap-2">
                  <i className="fa-regular fa-list-check text-[#0d3836]" aria-hidden="true" />
                  Hvad kan indgå i den almindelige privatrengøring?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Grundig støvsugning og vask af gulve i alle rum</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Aftørring af frie overflader, borde og vindueskarme</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Rengøring af køkkenbord, vask og kogeplader</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Afkalkning og rengøring af håndvask, armatur og toilet</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Aftørring af dørhåndtag, kontakter og lister</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Tømning af affaldsspande efter aftale</span>
                  </div>
                </div>
              </div>

              {/* Hvad koster det sektion */}
              <div className="rounded-2xl border border-[#0d3836]/15 bg-[#0d3836]/5 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                  Hvad koster privat rengøring?
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Prisen afhænger af boligens størrelse, omfang, hvor ofte der skal gøres rent og dine konkrete ønsker. Vi tager udgangspunkt i den enkelte opgave, så du får en løsning og en pris, der passer til dit hjem.
                </p>
                <div className="mt-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3836] hover:text-[#154e4a] transition-colors"
                  >
                    <span>Få et uforpligtende tilbud på rengøring</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Hurtig kontakt info sidebar */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display">
                  Direkte kontakt
                </h3>
                <p className="text-sm text-slate-600">
                  Har du spørgsmål til rengøring af dit hjem, eller ønsker du at høre mere om mulighederne? Ring eller skriv direkte:
                </p>

                <div className="space-y-4 pt-2">
                  <a
                    href="tel:31341632"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ring til os</p>
                      <p className="text-base font-bold text-slate-900 no-break">31 34 16 32</p>
                    </div>
                  </a>

                  <a
                    href="mailto:havdruprengoring@gmail.com"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Send en e-mail</p>
                      <p className="text-sm font-semibold text-slate-900 no-break">havdruprengoring@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100 text-xs text-slate-600 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-location-dot text-[#0d3836]" aria-hidden="true" />
                    <span>Lokal forankring i Havdrup (Sivvænget 6)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-shield-check text-[#0d3836]" aria-hidden="true" />
                    <span>Gensidig tillid og faste aftaler</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
